/**
 * Pre-mount setup for Surface 1 hosted walkthrough — writes synthetic
 * audit-chain records from `@shn/phg-client-demo-data`'s
 * `getSurfaceOneSeed()` into a `MockProjectionServiceClient` via the
 * production `writePatientProjectionRecord` orchestration (encrypts
 * records under the active-session KEK; same code path as production).
 *
 * The wrapper-injection seam (PR #210 concern #3 disposition) requires
 * this pre-mount setup so the production `<PatientProjectionView />`
 * code path consumes pre-encrypted synthetic records via the canonical
 * KEK-borrow + DEK-derive + decrypt pipeline. Per §5.4 architectural-
 * coherence test: same code, different config — no "demo mode" branch
 * inside the PHG client.
 */

import {
  getSurfaceOneSeed,
  type SyntheticHolder,
} from '@shn/phg-client-demo-data';
import type { SessionResidentDeps } from '@shn/phg-client-web';
import { MockProjectionServiceClient } from '@shn/phg-client-web/api';
import { EnvelopeCryptoAdapter } from '@shn/phg-client-web/crypto';
import {
  encodeContent,
  InMemoryRenderingTaxonomyV0,
  writePatientProjectionRecord,
} from '@shn/phg-client-web/projection';
import { ActiveSession } from '@shn/phg-client-web/session';
import type { KekId, TenantId } from '@shn/substrate-types';

/**
 * Surface 1 demo subject identity — the persona's `subjectPciHash`
 * threaded into ActiveSession + every `writePatientProjectionRecord`
 * call. Stable across the walkthrough so `<PatientProjectionView />`
 * fetches the patient's records via the canonical subject-scoped query.
 */
const DEMO_SUBJECT_PCI_HASH = 'demo-subject-pci-hash-v1';
const DEMO_KEK_ID = 'kek_demo_surface_one_v1' as KekId;

/**
 * Synthetic tenant identifier. v1 records OMIT tenantId per
 * substrate-types `AuditRecord` Tier B retrofit framing (single-tenant
 * deployment); the orchestrator's `WritePatientProjectionRecordInput`
 * accepts `undefined` so the demo composes cleanly.
 */
const DEMO_TENANT_ID: TenantId | undefined = undefined;

export interface SurfaceOnePreMountResult {
  readonly sessionDeps: SessionResidentDeps;
  readonly holders: ReadonlyArray<SyntheticHolder>;
  readonly recordsWritten: number;
}

/**
 * Pre-mount setup. Constructs session-resident deps + writes synthetic
 * audit-chain records into the projection store via the production
 * write pipeline. Returns the pre-built deps for injection into
 * `<App injectedSessionResidentDeps={...} />`.
 *
 * KEK is generated as `extractable: false` per
 * `feedback_phg_client_active_session_crypto` doctrine — same
 * runtime-enforcement as production. Surface 1 has no real PHI;
 * `extractable: false` is the doctrine, not a privacy mechanism here.
 */
export async function preMountSurfaceOne(): Promise<SurfaceOnePreMountResult> {
  const adapter = new EnvelopeCryptoAdapter();
  const projection = new MockProjectionServiceClient();
  const taxonomy = new InMemoryRenderingTaxonomyV0();

  const session = new ActiveSession({
    subject: { subjectPciHash: DEMO_SUBJECT_PCI_HASH },
  });
  const kek = await adapter.generateRandomKey({ extractable: false });
  session.loadUnwrappedKek({ kekId: DEMO_KEK_ID, key: kek });

  const seed = getSurfaceOneSeed();

  let recordsWritten = 0;
  for (const synthetic of seed.auditChainRecords) {
    const contentPlaintext = encodeContent({
      kind: 'audit-chain',
      auditRecord: synthetic.auditRecord,
    });
    await writePatientProjectionRecord({
      input: {
        tenantId: DEMO_TENANT_ID,
        subjectPciHash: DEMO_SUBJECT_PCI_HASH,
        recordId: `proj_${synthetic.auditRecord.recordId}`,
        sourceAuditRecordId: synthetic.auditRecord.recordId,
        contentCategory: 'operation-record',
        projectionAnchoredAt: synthetic.auditRecord.recordedAt,
        sourceAuditAnchoredAt: synthetic.auditRecord.recordedAt,
        contentPlaintext,
        unwrappedKek: kek,
        kekId: DEMO_KEK_ID,
        kekVersion: 1,
      },
      adapter,
      client: projection,
    });
    recordsWritten += 1;
  }

  return {
    sessionDeps: {
      activeSession: session,
      cryptoAdapter: adapter,
      projectionService: projection,
      taxonomy,
    },
    holders: seed.holders,
    recordsWritten,
  };
}
